import { supabase } from '../supabase';

const AVATARS_BUCKET = 'avatars';
const MAX_AVATAR_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * Upload user avatar to Storage and return public URL
 * Path: avatars/{userId}/avatar.{ext}
 */
export const uploadAvatar = async (userId, file) => {
  if (!file || !userId) throw new Error('File and userId are required');

  const type = file.type?.toLowerCase();
  if (!ALLOWED_TYPES.includes(type)) {
    throw new Error('Allowed types: JPEG, PNG, WebP, GIF');
  }
  if (file.size > MAX_AVATAR_SIZE_BYTES) {
    throw new Error('Max size 2 MB');
  }

  const ext = type === 'image/jpeg' ? 'jpg' : type.split('/')[1] || 'jpg';
  const path = `${userId}/avatar.${ext}`;

  // Upload File as-is; bucket "Allowed MIME types" must include image/jpeg, image/png, image/webp, image/gif (not only image/*)
  const { error: uploadError } = await supabase.storage
    .from(AVATARS_BUCKET)
    .upload(path, file, { upsert: true });

  if (uploadError) {
    const message = uploadError.message || uploadError.error || 'Upload failed';
    const err = new Error(message);
    err.details = uploadError;
    throw err;
  }

  const { data: { publicUrl } } = supabase.storage
    .from(AVATARS_BUCKET)
    .getPublicUrl(path);

  return publicUrl;
};

/**
 * Extract storage path from public URL and delete the object
 * URL format: .../object/public/avatars/{path}
 */
const getPathFromPublicUrl = (publicUrl) => {
  if (!publicUrl) return null;
  const match = publicUrl.match(/\/avatars\/(.+)$/);
  return match ? match[1] : null;
};

/**
 * Remove avatar from Storage by its public URL, then clear profile.avatar_url
 */
export const removeAvatar = async (avatarUrl) => {
  if (!avatarUrl) return;
  const path = getPathFromPublicUrl(avatarUrl);
  if (!path) return;
  await supabase.storage.from(AVATARS_BUCKET).remove([path]);
};
